import { motion } from 'framer-motion';
import { Check, Camera, Printer, Star, Image, Palette, Users, Monitor } from 'lucide-react';

const PRICING_TIERS = [
  {
    name: 'BASIC',
    price: '150',
    unit: '€',
    subtitle: 'Keine weiteren Kosten',
    features: [
      'Fotobox für Ihre Veranstaltung',
      'Alle Bilder auf USB-Stick',
      'Oder als Download verfügbar',
      'Basis-Accessoires inklusive'
    ],
    highlight: false,
    icon: Camera
  },
  {
    name: 'PREMIUM',
    price: '200',
    unit: '€',
    subtitle: '+ 50ct pro Ausdruck',
    features: [
      'Alles aus dem BASIC-Paket',
      'Fotodrucker vor Ort',
      'Bilder sofort zum Mitnehmen',
      'Hochwertige Fotoqualität'
    ],
    highlight: false,
    icon: Printer
  },
  {
    name: 'ALL IN',
    price: '300',
    unit: '€',
    subtitle: 'Druck-Flatrate inklusive!',
    features: [
      'Alles aus dem PREMIUM-Paket',
      'Unbegrenzte Ausdrucke',
      'Das Rundum-sorglos-Paket',
      'Keine versteckten Kosten'
    ],
    highlight: true,
    icon: Star
  }
];

const EXTRAS = [
  {
    title: 'Onlinegalerie',
    price: '50€',
    description: 'Passwortgeschützte Galerie für Ihre Gäste',
    icon: Monitor
  },
  {
    title: 'Event-Logo',
    price: '50€',
    description: 'Ihr Logo auf allen Bildern',
    icon: Image
  },
  {
    title: 'Hintergrundleinwand',
    price: '20€',
    description: 'Professioneller Hintergrund für Ihre Bilder',
    icon: Palette
  },
  {
    title: 'Betreuung',
    price: '50€/h',
    description: 'Wir kümmern uns um alles',
    icon: Users
  },
  {
    title: 'Premium Accessoires',
    price: '20€',
    description: 'Hochwertige Hüte, Brillen & mehr',
    icon: Star
  }
];

export const PricingSection = () => {
  return (
    <section id="pricing" className="py-16 md:py-20 px-6 bg-gradient-to-b from-white to-[#F0F9F8]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#3ECDC3] font-semibold text-sm tracking-widest uppercase block mb-4">
            Faire Preise
          </span>
          <h2 
            className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4"
            
            data-testid="pricing-headline"
          >
            Geile Kiste,<br />
            <span className="text-[#3ECDC3]">geile Preise!</span>
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Profitieren Sie durch unsere einfache und faire Preisgestaltung
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-24" data-testid="pricing-cards">
          {PRICING_TIERS.map((tier, index) => {
            const IconComponent = tier.icon;
            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`pricing-card p-8 ${
                  tier.highlight 
                    ? 'featured bg-white border-2 border-[#3ECDC3]' 
                    : 'bg-white border border-gray-200 hover:border-[#3ECDC3]/50'
                } transition-all duration-500`}
                data-testid={`pricing-card-${tier.name.toLowerCase()}`}
              >
                {tier.highlight && (
                  <div className="text-[#3ECDC3] text-xs font-bold tracking-widest uppercase mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 fill-[#3ECDC3]" />
                    Beliebteste Wahl
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-3 rounded-xl ${tier.highlight ? 'bg-[#3ECDC3]/10' : 'bg-gray-100'}`}>
                    <IconComponent className="w-6 h-6 text-[#3ECDC3]" />
                  </div>
                  <h3 
                    className="text-2xl font-bold text-gray-900"
                    
                  >
                    {tier.name}
                  </h3>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-gray-900" >
                      {tier.price}
                    </span>
                    <span className="text-2xl text-gray-500">{tier.unit}</span>
                  </div>
                  <p className="text-sm text-[#3ECDC3] font-medium mt-2">{tier.subtitle}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#3ECDC3]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#3ECDC3]" />
                      </div>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`block w-full text-center py-4 rounded-xl font-bold tracking-wide transition-all cursor-pointer ${
                    tier.highlight
                      ? 'bg-[#3ECDC3] text-white hover:bg-[#2BB8AF] shadow-lg shadow-[#3ECDC3]/30'
                      : 'border-2 border-[#3ECDC3] text-[#3ECDC3] hover:bg-[#3ECDC3] hover:text-white'
                  }`}
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                >
                  Anfragen
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Extras Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h3 
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center"
            
          >
            Optional <span className="text-[#3ECDC3]">buchbar</span>
          </h3>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="extras-grid">
          {EXTRAS.map((extra, index) => {
            const IconComponent = extra.icon;
            return (
              <motion.div
                key={extra.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#3ECDC3] hover:shadow-lg transition-all group"
                data-testid={`extra-${extra.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#3ECDC3]/10 flex items-center justify-center group-hover:bg-[#3ECDC3]/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-[#3ECDC3]" />
                  </div>
                  <span className="text-[#3ECDC3] font-bold text-lg">{extra.price}</span>
                </div>
                <h4 className="text-gray-900 font-bold mb-2">{extra.title}</h4>
                <p className="text-gray-500 text-sm">{extra.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
